// @ts-nocheck

import { colors } from '@lmc/common/constants'
import {
  EMAR_STOCK_SUMMARY_ITEM_ID,
  ITEM_ID_SHORT_SEIZURE
} from '@lmc/common/types'
import { Chevron, Collapse, Panel } from '@lmc/ui-kit'
import { Switch } from 'antd'
import _, { chain, filter, includes } from 'lodash'
import React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { DotIconSection } from '../../generic'
import DotIcon from '../../generic/DotIcon/DotIcon'

export class LogCategoryItemField extends React.Component {
  state = {}

  toggleItem = (itemId) => {
    // push or pull from array
    const { values, formKey } = this.props
    const prev = values[formKey] || []
    if (includes(prev, itemId)) {
      this.props.onChange({
        key: formKey,
        value: filter(prev, (i) => i !== itemId)
      })
    } else {
      // push
      this.props.onChange({
        key: formKey,
        value: [...prev, itemId]
      })
    }
  }

  /**
   * CM-4043. Inverted against `toggleItem` on purpose: `logCategoryItems` is a
   * list of ENABLED items, `hiddenCustomLogCategories` a list of HIDDEN
   * categories. That inversion is what makes absence mean "on", so existing
   * sites keep their Custom logs with no backfill (AC5.1).
   */
  toggleCustom = (categoryId) => {
    const prev = this.props.values.hiddenCustomLogCategories || []
    this.props.onChange({
      key: 'hiddenCustomLogCategories',
      value: includes(prev, categoryId)
        ? filter(prev, (id) => id !== categoryId)
        : [...prev, categoryId]
    })
  }

  isCustomActive = (categoryId) =>
    !includes(this.props.values.hiddenCustomLogCategories || [], categoryId)

  isFamilySurface = () => this.props.formKey === 'familyLogCategoryItems'

  surfaceLabel = () => (this.isFamilySurface() ? 'Family Portal' : 'App')

  filterItems = (items, selectedCategory, hideShortSeizure) => {
    return chain(items)
      .filter(
        (item) =>
          item.category === selectedCategory &&
          (hideShortSeizure ? item?.id !== ITEM_ID_SHORT_SEIZURE : true) &&
          item?.itemId !== EMAR_STOCK_SUMMARY_ITEM_ID
      )
      .orderBy('name')
      .value()
  }

  renderCategoryItems = (category) => {
    const { values, formKey, hideShortSeizure, showCustomLogToggle } =
      this.props

    const items = this.filterItems(
      this.props.items,
      category.id,
      hideShortSeizure
    )

    if (!items) {
      return null
    }

    const isActive = (itemId) => includes(values[formKey], itemId)

    return (
      <CategoryItemsContainer>
        {items.map((item) => (
          <ItemRow key={item.id} data-testid={`log-option-row-${item.id}`}>
            <IconContainer>
              <DotIcon
                color={
                  isActive(item.id) ? item.color || category.color : colors.grey
                }
                url={item.icon}
              />
            </IconContainer>
            <ContentContainer>
              <ItemName
                isActive={isActive(item.id)}
                data-testid="log-option-name"
              >
                {item.name}
              </ItemName>
              <ItemNote isActive={isActive(item.id)}>{item.note}</ItemNote>
            </ContentContainer>
            <SwitchContainer>
              <Switch
                onChange={() => this.toggleItem(item.id)}
                checked={isActive(item.id)}
                aria-label={`Show ${item.name} in ${this.surfaceLabel()}`}
              />
            </SwitchContainer>
          </ItemRow>
        ))}
        {/*
          CM-4043: appended, never sorted in among the defaults (AC1.1), and
          rendered even when the category has no default logs left — that row is
          the only way to bring the category back.

          Not offered on the Family Portal variant: family members cannot see
          custom logs at all, so a toggle there would be a lie.
        */}
        {showCustomLogToggle && !this.isFamilySurface() && (
          <ItemRow data-testid={`log-option-row-custom-${category.id}`}>
            <IconContainer>
              <DotIcon
                color={
                  this.isCustomActive(category.id)
                    ? category.color
                    : colors.grey
                }
                url={category.icon}
              />
            </IconContainer>
            <ContentContainer>
              <ItemName
                isActive={this.isCustomActive(category.id)}
                data-testid="log-option-name"
              >
                Custom log
              </ItemName>
            </ContentContainer>
            <SwitchContainer>
              <Switch
                onChange={() => this.toggleCustom(category.id)}
                checked={this.isCustomActive(category.id)}
                aria-label={`Show Custom log in ${this.surfaceLabel()}`}
              />
            </SwitchContainer>
          </ItemRow>
        )}
      </CategoryItemsContainer>
    )
  }

  render() {
    const { categories, items } = this.props

    const isFamilyLogSettings = this.isFamilySurface()

    if (!categories || !items) {
      return null
    }
    return (
      <div>
        <LabelContainer>
          <Label isFamilyLogSettings={isFamilyLogSettings}>
            Show in {this.surfaceLabel()}
          </Label>
        </LabelContainer>
        <StyledCollapse
          expandIconPosition="end"
          expandIcon={(opt) => {
            return opt.isActive ? (
              <Chevron direction="down" />
            ) : (
              <Chevron direction="right" />
            )
          }}
        >
          {categories.map((category) => (
            <Panel
              key={category.id}
              header={
                <DotIconSection
                  text={category.name}
                  iconUrl={category.icon}
                  iconColor={category.color}
                />
              }
            >
              {this.renderCategoryItems(category)}
            </Panel>
          ))}
        </StyledCollapse>
      </div>
    )
  }
}

const StyledCollapse = styled(Collapse)`
  &&.ant-collapse.ant-collapse-icon-position-start,  &&.ant-collapse.ant-collapse-icon-position-end {
  border-color: ${(props) => props.theme.color.neutral.color8};

  .ant-collapse-item, .ant-collapse-header, .ant-collapse-content {
    border-color: ${(props) => props.theme.color.neutral.color8};
  }

  .ant-collapse-header {
    padding: 8px 16px;
  }
   
  .ant-collapse-header-text{
    li {padding-left: 0px !important;}
  }

  .ant-collapse-item {
    .ant-collapse-header {
      align-items: baseline;
    }
  }
`

const CategoryItemsContainer = styled.div`
  padding-left: 16px;
  width: 100%;
  margin-bottom: 25px;
`

const ItemRow = styled.div`
  padding-top: 8px;
  padding-bottom: 10px;
  display: flex;
`

const IconContainer = styled.div`
  flex: 1;
  max-width: 40px;
`

const ContentContainer = styled.div`
  flex: 10;
  margin-top: 6px;
  padding-left: 16px;
`

const ItemName = styled.div<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? 'black' : colors.grey)};
  font-size: 18px;
  font-weight: bold;
`

const ItemNote = styled.div<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? 'black' : colors.grey)};
`

const SwitchContainer = styled.div`
  flex: 0;
  margin: 8px 0px 0 40px;
`

const LabelContainer = styled.p`
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 6px;
  display: flex;
  justify-content: flex-end;
`

const Label = styled.div<{ isFamilyLogSettings: boolean }>`
  margin-right: ${(props) => (props.isFamilyLogSettings ? 5 : 33)}px;
`

const mapStateToProps = (state) => {
  return {
    categories: chain(state.data['log-categories'])
      .orderBy('name')
      .filter(({ itemId }) => itemId !== 'bodyMaps')
      .value(),
    items: _.filter(state.data['log-category-items'], { visible: true })
  }
}

export default connect(mapStateToProps, null)(LogCategoryItemField)
